import { useState, useEffect } from 'react';
import { X, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { createProperty } from '@/lib/api';
import { toast } from 'sonner';

function StepChip({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 mr-1.5 mb-1">
      {label}
    </span>
  );
}

export function PropertyCreatePanel({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [availableParking, setAvailableParking] = useState('');
  const [petsPermitted, setPetsPermitted] = useState(false);
  const [petNotes, setPetNotes] = useState('');
  const [amenityInput, setAmenityInput] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  useEffect(() => { if (open) resetForm(); }, [open]);

  const resetForm = () => {
    setStep(1);
    setName(''); setAddress('');
    setBuildingId(''); setOwnerName(''); setOwnerPhone(''); setOwnerEmail('');
    setAvailableParking(''); setPetsPermitted(false); setPetNotes('');
    setAmenityInput(''); setAmenities([]);
    setAdditionalNotes('');
    setSaving(false); setShowDiscard(false);
  };

  const handleClose = () => {
    if (step > 1) setShowDiscard(true);
    else onClose();
  };

  const addAmenity = () => {
    if (amenityInput.trim()) {
      setAmenities(prev => [...prev, amenityInput.trim()]);
      setAmenityInput('');
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || !address.trim()) { toast.error('Name and address are required'); return; }
    setSaving(true);
    try {
      await createProperty({
        name: name.trim(), address: address.trim(),
        building_id: buildingId !== '' ? parseInt(buildingId, 10) : null,
        owner_manager_name: ownerName, owner_manager_phone: ownerPhone,
        owner_manager_email: ownerEmail, available_parking: availableParking,
        pets_permitted: petsPermitted, pet_notes: petNotes,
        building_amenities: amenities, additional_notes: additionalNotes,
      });
      toast.success('Property created');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create property');
    } finally { setSaving(false); }
  };

  const summaryChips = [];
  if (name) summaryChips.push(name);
  if (address && step > 1) summaryChips.push(address);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-full bg-white z-50 shadow-xl flex flex-col"
        style={{ borderLeft: '0.5px solid #e5e7eb', animation: 'slideInRight 200ms ease-out' }}
        data-testid="property-create-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-[15px] font-semibold text-gray-900">New Property</h2>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-gray-100" data-testid="property-create-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {showDiscard && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-[13px] text-amber-700">Discard changes?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDiscard(false)}>Keep editing</Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { resetForm(); onClose(); }}>Discard</Button>
            </div>
          </div>
        )}

        {summaryChips.length > 0 && (
          <div className="px-5 pt-3 pb-1 flex flex-wrap">
            {summaryChips.map((c, i) => <StepChip key={i} label={c} />)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* STEP 1: Name + Address */}
          <div>
            <p className="text-[13px] font-semibold text-gray-700 mb-3">1. Property basics</p>
            <div className="space-y-3">
              <div>
                <Label className="text-[12px] text-gray-500">Property Name *</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Downtown Apartments" className="h-9 text-[13px] mt-1" data-testid="property-create-name" />
              </div>
              <div>
                <Label className="text-[12px] text-gray-500">Address *</Label>
                <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. 123 Main St" className="h-9 text-[13px] mt-1" data-testid="property-create-address" />
              </div>
            </div>
            {step === 1 && (
              <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => {
                if (!name.trim() || !address.trim()) { toast.error('Name and address are required'); return; }
                setStep(2);
              }} data-testid="property-step1-next">
                Next <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>

          {/* STEP 2: Building + Owner */}
          {step >= 2 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">2. Owner & building info</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Building ID</Label>
                  <Input type="number" value={buildingId} onChange={e => setBuildingId(e.target.value)} placeholder="e.g. 1, 2, 3" className="h-9 text-[13px] mt-1" />
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Owner/Manager Name</Label>
                  <Input value={ownerName} onChange={e => setOwnerName(e.target.value)} className="h-9 text-[13px] mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[12px] text-gray-500">Phone</Label>
                    <Input value={ownerPhone} onChange={e => setOwnerPhone(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                  <div>
                    <Label className="text-[12px] text-gray-500">Email</Label>
                    <Input type="email" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} className="h-9 text-[13px] mt-1" />
                  </div>
                </div>
              </div>
              {step === 2 && (
                <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => setStep(3)} data-testid="property-step2-next">
                  Next <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {/* STEP 3: Extras */}
          {step >= 3 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">3. Additional details</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Available Parking</Label>
                  <Input value={availableParking} onChange={e => setAvailableParking(e.target.value)} placeholder="e.g. 2 spots, street parking" className="h-9 text-[13px] mt-1" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20">
                  <Switch checked={petsPermitted} onCheckedChange={setPetsPermitted} data-testid="property-create-pets-toggle" />
                  <Label className="text-[12px] cursor-pointer">{petsPermitted ? 'Pets Permitted' : 'No Pets'}</Label>
                </div>
                {petsPermitted && (
                  <div>
                    <Label className="text-[12px] text-gray-500">Pet Notes</Label>
                    <Input value={petNotes} onChange={e => setPetNotes(e.target.value)} placeholder="Weight limits, breed restrictions..." className="h-9 text-[13px] mt-1" />
                  </div>
                )}
                <div>
                  <Label className="text-[12px] text-gray-500">Building Amenities</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={amenityInput} onChange={e => setAmenityInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                      placeholder="Add and press Enter" className="h-9 text-[13px]" />
                    <Button type="button" variant="secondary" size="sm" className="h-9" onClick={addAmenity}>Add</Button>
                  </div>
                  {amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {amenities.map((a, i) => (
                        <Badge key={i} variant="secondary" className="gap-1 text-[11px]">
                          {a}
                          <button onClick={() => setAmenities(prev => prev.filter((_, j) => j !== i))} className="ml-0.5 text-gray-400 hover:text-red-500">&times;</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Additional Notes</Label>
                  <Textarea value={additionalNotes} onChange={e => setAdditionalNotes(e.target.value)} className="text-[13px] mt-1" rows={3} />
                </div>
              </div>
            </div>
          )}
        </div>

        {step >= 3 && (
          <div className="px-5 py-3 border-t bg-gray-50">
            <Button className="w-full h-9 text-[13px]" disabled={!name.trim() || !address.trim() || saving} onClick={handleCreate} data-testid="property-create-submit">
              {saving ? 'Creating...' : 'Create property'}
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
